<template>
  <article id="PROFILE_PASSWORD" class="container-fixed offset-bottom-4 offset-top-2">
    <VNotification v-if="!isAuthTypeLogin" heading="common.headerError" inner-class="offset-bottom-1" type="ERROR" closable @close="onAbort">
      {{ $to('common.passwordChangeNotAllowed') }}
    </VNotification>
    <VForm v-else-if="form" :form="form" @submit="onSubmit">
      <VConfirmation v-model="confirmation" :heading="headerText" :is-busy="isLoading" no-focus-restore class="offset-top-2" @abort="onAbort">
        <template #prompt>
          <VInput
            name="oldPassword"
            class="text-left"
            type="password"
            :placeholder="$to('common.placeholder_oldPassword')"
            :label="$to('common.oldPassword')"
            required
          />
          <VInput
            name="newPassword"
            class="text-left"
            type="password"
            :placeholder="$to('common.placeholder_newPassword')"
            :label="$to('common.newPassword')"
            required
            @focus="onShowPasswordHint"
          />
          <transition name="fade" appear>
            <VNotification
              v-if="passwordHint"
              heading="Information"
              inner-class="inner-info"
              type="INFO"
              class="notification-info"
              :supplier="informationText"
              closable
              @close="onHidePasswordHint"
            />
          </transition>
          <VInput
            name="verifyNewPassword"
            type="password"
            class="text-left"
            :placeholder="$to('common.placeholder_reentered')"
            :label="$to('common.reentered')"
            required
          />
        </template>
        <template #content>
          <VButton type="submit" class="btn-small btn-brand" :disabled="isLoading">
            {{ $to('common.save') }}
          </VButton>
          <VButton type="button" class="offset-left-1 btn-small btn-default" :disabled="isLoading" @click="onAbort">
            {{ $to('common.abort') }}
          </VButton>
        </template>

        <template #success>
          <div class="inner">
            <div class="notification-content media-left">
              {{ $to('common.passwordChangeSuccessful') }}
              <VButton v-focus type="button" class="offset-left-2 btn-small btn-default" @click="onAbort">
                {{ $t('common.passwordChangeSuccessful_button_label') }}
              </VButton>
            </div>
          </div>
        </template>

        <template #error>
          <div class="inner">
            <div class="notification-content">
              <div class="offset-top-1" v-html="$to('common.passwordChangeFailed')" />
              <VButton v-focus type="button" class="offset-top-2 btn-small btn-default" title="no" @click="onAbort">{{ $t('common.retry') }}</VButton>
            </div>
          </div>
        </template>
      </VConfirmation>
    </VForm>
  </article>
</template>

<script>
import TitleMixin from '@/mixins/title';
import VConfirmation from '@/components/Confirmation';
import VNotification from '@/components/Notification';
import VForm from '@/components/Form/Form';
import VInput from '@/components/Form/Input';
import VButton from '@/components/Button';
import { focusFirst, hasMaxLength, hasMinLength, initFields, resetValidations, resetValues } from '@/utils/form';
import Proxy from '@/proxies/PasswordProxy';
import { HOME } from '@/router/route-types';
import { mapGetters } from 'vuex';
import focus from '@/directives/focus';

export default {
  name: 'UserPassword',

  components: {
    VConfirmation,
    VNotification,
    VForm,
    VInput,
    VButton,
  },

  directives: { focus },

  mixins: [TitleMixin],

  data() {
    return {
      isLoading: true,
      confirmation: null,
      form: null,
      constraints: null,
      passwordHint: false,
    };
  },

  computed: {
    ...mapGetters('system', ['isAuthTypeLogin']),

    headerText() {
      return this.confirmation?.message?.type === 'ERROR' ? this.$to('common.headerError') : this.$to('common.passwordHeading');
    },

    informationText() {
      return () =>
        this.$tc('common.passwordInfoText', this.constraints.fulfillNumberOfCategories === 4 ? 0 : this.constraints.fulfillNumberOfCategories, {
          minLength: this.constraints.minLength,
          maxLength: this.constraints.maxLength,
          fulfillCategories: this.constraints.fulfillNumberOfCategories,
          specialChars: this.constraints.specialCharacters.split('').join(' '),
        });
    },
  },

  async created() {
    this.constraints = await new Proxy().getPasswordConstraints();

    this.form = initFields(
      {
        name: 'oldPassword',
        validators: [
          {
            handler: hasMinLength(1),
            hint: 'common.err_password_required',
          },
        ],
      },
      {
        name: 'newPassword',
        validators: [
          {
            handler: hasMinLength(this.constraints.minLength),
            hint: this.$t('common.err_passwordMinLength', { minLength: this.constraints.minLength }),
          },
          {
            handler: hasMaxLength(this.constraints.maxLength),
            hint: this.$t('common.err_passwordMaxLength', { maxLength: this.constraints.maxLength }),
          },
          {
            handler: this.validatePasswordConstraints,
            hint: 'common.err_passwordConstraints',
          },
          {
            handler: this.validCharactersOnly,
            hint: 'common.err_unknownCharacters',
          },
          {
            handler: this.validateOldAndNewPasswordDiffer,
            hint: 'common.err_passwordNotChanged',
          },
        ],
      },
      {
        name: 'verifyNewPassword',
        validators: [
          {
            handler: hasMinLength(1),
            hint: 'common.err_password_required',
          },
          {
            handler: this.validateNewAndVerificationMatch,
            hint: 'common.err_passwordMismatch',
          },
        ],
      }
    );
    this.isLoading = false;
  },

  methods: {
    onShowPasswordHint() {
      this.passwordHint = true;
    },

    onHidePasswordHint() {
      this.passwordHint = false;
    },

    onAbort() {
      if (this.confirmation === null || this.confirmation?.message?.type === 'SUCCESS') {
        this.$router.push(HOME);
      } else {
        resetValues(this.form);
        resetValidations(this.form);
        focusFirst(this.form);
        this.confirmation = null;
      }
    },

    onSubmit({ values }) {
      this.isLoading = true;
      new Proxy()
        .changePassword({ oldPassword: values.oldPassword, newPassword: values.newPassword })
        .then(() => {
          this.confirmation = { message: { type: 'SUCCESS', message: 'Password changed successfully' } };
        })
        .catch(err => (this.confirmation = { message: { type: 'ERROR', message: err.statusText } }))
        .finally(() => (this.isLoading = false));
    },

    validateOldAndNewPasswordDiffer(val, state) {
      return state.oldPassword.value !== val;
    },

    validateNewAndVerificationMatch(val, state) {
      return state.newPassword.value === val;
    },

    validCharactersOnly(val) {
      // we replace all characters which have a special meaning within [] brackets of a regexp
      const pattern = '^[A-Za-z0-9' + this.constraints.specialCharacters.replace(/[-\\^[\]]/g, '\\$&') + ']+$';
      const regexp = new RegExp(pattern);
      return regexp.test(val);
    },

    validatePasswordConstraints(val) {
      let counter = 0;
      if (/[A-Z]/.test(val)) counter++;
      if (/[a-z]/.test(val)) counter++;
      if (/[0-9]/.test(val)) counter++;
      if (val.split('').find(c => this.constraints.specialCharacters.includes(c))) {
        counter++;
      }

      return counter >= this.constraints.fulfillNumberOfCategories;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.text-left {
  text-align: left;
}

.notification-info {
  background-color: $color-gray-light;
}

.inner-info::v-deep > .inner > .notification-content {
  ul {
    margin-bottom: 0;
  }
}
</style>
